/**
 * @fileoverview ContactNotesFeed — thin container wiring the shared
 * `NotesFeedView` to the **job-contact** notes endpoints.
 *
 * Owns the Convex data (`api.contacts.notes.*` + `notesSummary.*`) and
 * the summarise busy-state; all presentation lives in the shared
 * `src/components/contacts/notes/` components so the job and firm feeds
 * stay identical.
 */

import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { NotesFeedView } from "@/components/contacts/notes/NotesFeedView";
import type { FeedNote } from "@/components/contacts/notes/NoteRow";

interface NotesSummaryFromContact {
  text: string;
  generatedAt: number;
  generatedBy: Id<"users">;
  noteCount: number;
  notesHash: string;
  model: string;
}

interface ContactNotesFeedProps {
  contactId: Id<"jobContacts">;
  /** Read-only when the parent contact is merged. */
  readOnly?: boolean;
  /** The current authenticated user (used to decide which rows are editable). */
  currentUserId?: Id<"users">;
  /** Cached AI summary read from the parent contact query. */
  notesSummary?: NotesSummaryFromContact;
  /** Live notes count read from the parent contact query (for stale detection). */
  notesCount: number;
  /** Triggered by the Chat button in the header. */
  onOpenChat: () => void;
  /** Hide the notes-only AI summary when a parent surface already shows the
   *  combined mail + notes insight (MOR-841 consolidated tab). */
  showSummary?: boolean;
}

export function ContactNotesFeed({
  contactId,
  readOnly = false,
  currentUserId,
  notesSummary,
  notesCount,
  onOpenChat,
  showSummary = true,
}: ContactNotesFeedProps) {
  const { data: notes, isLoading } = useQuery({
    ...convexQuery(api.contacts.notes.listContactNotes, { contactId }),
  });

  const createNote = useConvexMutation(api.contacts.notes.createContactNote);
  const updateNote = useConvexMutation(api.contacts.notes.updateContactNote);
  const deleteNote = useConvexMutation(api.contacts.notes.deleteContactNote);
  const summariseAction = useConvexAction(
    api.contacts.notesSummary.summariseContactNotes,
  );

  const { mutateAsync: doSummarise } = useMutation({
    mutationFn: summariseAction,
  });
  const [isSummarising, setIsSummarising] = useState(false);

  const handlePost = useCallback(
    async (content: string) => {
      await createNote({ contactId, content });
    },
    [contactId, createNote],
  );

  const handleSaveNote = useCallback(
    async (noteId: string, content: string) => {
      await updateNote({ noteId: noteId as Id<"jobContactNotes">, content });
    },
    [updateNote],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      await deleteNote({ noteId: noteId as Id<"jobContactNotes"> });
    },
    [deleteNote],
  );

  const handleGenerateSummary = useCallback(async () => {
    if (isSummarising) return;
    if (notesCount === 0) {
      toast.info("Post a note first, then I can summarise the feed.");
      return;
    }
    setIsSummarising(true);
    try {
      const result = await doSummarise({ contactId });
      toast.success(result.cached ? "Summary up to date" : "Summary ready");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not generate the summary";
      toast.error(message);
    } finally {
      setIsSummarising(false);
    }
  }, [contactId, notesCount, isSummarising, doSummarise]);

  const handleRefreshSummary = useCallback(async () => {
    if (isSummarising) return;
    setIsSummarising(true);
    try {
      await doSummarise({ contactId, force: true });
      toast.success("Summary refreshed");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not refresh the summary";
      toast.error(message);
    } finally {
      setIsSummarising(false);
    }
  }, [contactId, isSummarising, doSummarise]);

  const notesList: Array<FeedNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  return (
    <NotesFeedView
      notes={notesList}
      notesCount={notesCount}
      isLoading={isLoading}
      readOnly={readOnly}
      currentUserId={currentUserId}
      summary={notesSummary ?? null}
      isSummarising={isSummarising}
      showSummary={showSummary}
      showChat
      onOpenChat={onOpenChat}
      onPost={handlePost}
      onSaveNote={handleSaveNote}
      onDeleteNote={handleDeleteNote}
      onGenerateSummary={() => void handleGenerateSummary()}
      onRefreshSummary={() => void handleRefreshSummary()}
    />
  );
}
